<template>
    <Head v-if="loaded">
        <title>{{ siteTitle }} | {{ pageTitle }}</title>
        <meta name="description" :content="pageDescription">
    </Head>
    <template v-if="loaded">
        <router-view/>
    </template>
</template>

<script>
    import PageMeta from '@/mixins/pageMeta'
    import dataStore from '@/store/data'
    import AOS from 'aos'
    import axios from 'axios'
    import {Head} from '@vueuse/head'

    export default {
        name: 'App',
        inject: ['dataStore'],
        mixins: [PageMeta],
        props: {
            sdk: null,
            errorHandler: null
        },
        components: {Head},
        data() {
            return {
                loaded: false
            }
        },
        methods: {
            async generateToken(){
                return this.sdk.generateToken()
                    .then(response => {
                        const token = response.token;
                        this.dataStore.setApiToken(token);
                        this.loadComponentData().then(() => {
                            this.loaded = true
                        })
                    }).catch(error => {
                        this.errorHandler.handleError(error);
                    })
            },
            async loadToken() {
                const apiToken = this.dataStore.getApiToken()

                if (apiToken) {
                    return this.sdk.getTokenByToken(apiToken)
                        .then(response => {
                            const token = response.token;
                            this.dataStore.setApiToken(token);
                            this.loadComponentData().then(() => {
                                this.loaded = true
                            })
                        }).catch(error => {
                            if (error.response.status === 404) {
                                this.generateToken();
                            }
                            this.errorHandler.handleError(error);
                        })
                }

                return this.generateToken()
            },
            async loadStore () {
                this.sdk.getStore(['features']).then(response => {
                    const store = response.store
                    this.dataStore.setStore(store);
                }).catch(error => {
                    this.errorHandler.handleError(error);
                });
            },
            async loadBasket () {
                const basketReference = this.dataStore.getBasketReference();

                if(basketReference){
                    return this.sdk.getBasketById(basketReference)
                        .then(response => {
                            const basket = response.basket;
                            this.dataStore.setBasket(basket);
                        }).catch(error => {
                            this.errorHandler.handleError(error);
                        })
                }
            },
            async loadSite () {
                return this.sdk.getSiteBySlug('website').then(response => {
                    const site = response.sites[0]
                    // Convert all the content to objects for ease
                    site.content.forEach(content => {
                        content.value = JSON.parse(content.value)
                    })
                    this.dataStore.setSite(site);
                    this.setupTheme(site)
                }).catch(error => {
                    this.errorHandler.handleError(error);
                });
            },
            setupTheme: function(site){
                let content = site.content.filter(content => content.content_type && content.content_type.key === 'site_settings')
                let theme = null

                if(content.length > 0) {
                    theme = content[0].value
                }

                // Append favicon
                let favicon = document.createElement('link')
                favicon.href = theme.favicon.url
                favicon.rel = 'icon'
                document.head.appendChild(favicon)

                // Append font and styles
                let font = document.createElement('link')
                font.href = theme.theme_font_url
                font.rel = 'stylesheet'
                document.head.appendChild(font)

                if(theme.analytics_scripts) {
                    let analytics = document.createElement('script')
                    analytics.src = theme.analytics_scripts
                    document.head.appendChild(analytics)
                }

                let cssString = ":root { " +
                    "--theme-body-font-family: {{theme_body_font_family}}; " +
                    "--theme-heading-font-family: {{theme_heading_font_family}}; " +
                    "--theme-body-background-colour: {{theme_body_background_colour}}; " +
                    "--theme-colour-black: {{theme_colour_black}};" +
                    "--theme-colour-primary: {{theme_colour_primary}};" +
                    "--theme-colour-secondary: {{theme_colour_secondary}};" +
                    "--theme-colour-tertiary: {{theme_colour_tertiary}};" +
                    "}";
                let styles = document.createElement('style')
                for (const [key, value] of Object.entries(theme)) {
                    cssString = cssString.replace('{{' + key + '}}', value)
                }
                styles.innerText = cssString
                document.head.appendChild(styles)
            },
            async loadComponentData(){
                return await axios.all([this.loadStore(), this.loadSite(), this.loadBasket()])
            },
        },
        created() {
            AOS.init({
                duration: 750,
                easing: 'ease-in-out-sine',
                once: true,
                offset: 60,
            });

            this.loadToken()

            const urlParams = new URLSearchParams(window.location.search);
            const partnerReference = urlParams.get('partner_reference');

            if (partnerReference) {
                this.dataStore.setPartnerReference(partnerReference)
            }
        }
    }
</script>